import type { IInventoryItem } from './inventory'
import type { ICurrencyMap } from './wallet'

export interface ITransaction {
    _id?: string
    user: string
    processed?: boolean
    type: TransactionType
    kind: TransactionKind
    currency: string
    amount: number
    items?: IInventoryItem[]
    data?: {
        id: string
        ref: string
    }

    wallet?: ICurrencyMap

    inventory?: {
        items: string[]
        value: number
    }

    createdAt?: Date
    updatedAt?: Date
}

export enum TransactionType {
    DEPOSIT = 'DEPOSIT',
    CASHOUT = 'CASHOUT',
    WITHDRAWAL = 'WITHDRAWAL',
    CASE_PURCHASE = 'CASE_PURCHASE',
    CASE_WINNINGS = 'CASE_WINNINGS',
    PACK_PURCHASE = 'PACK_PURCHASE',
    PACK_WINNINGS = 'PACK_WINNINGS',
    ITEMS_SALE = 'ITEMS_SALE',
    ITEMS_SOLD = 'ITEMS_SOLD',
    CARD_PURCHASE = 'CARD_PURCHASE',
    CARD_PURCHASED = 'CARD_PURCHASED',
    ITEMS_TRADE_IN = 'ITEMS_TRADE_IN',
    ITEMS_TRADE_OUT = 'ITEMS_TRADE_OUT',
    ITEMS_UPGRADE_IN = 'ITEMS_UPGRADE_IN',
    ITEMS_UPGRADE_OUT = 'ITEMS_UPGRADE_OUT',
    AFFILIATE_EARNINGS = 'AFFILIATE_EARNINGS',
    ADD_FUNDS = 'ADD_FUNDS',
    PROMO_CODE = 'PROMO_CODE',
    LEADERBOARD_PAYOUT = 'LEADERBOARD_PAYOUT',
    SHIPPING = 'SHIPPING',
    ADMIN_INVENTORY_ADD = 'ADMIN_INVENTORY_ADD',
    ADMIN_INVENTORY_REMOVE = 'ADMIN_INVENTORY_REMOVE',
    FREE_PACK_WINNINGS = 'FREE_PACK_WINNINGS',
    FREE_CASE_WINNINGS = 'FREE_CASE_WINNINGS'
}

export enum TransactionKind {
    BALANCE = 'BALANCE',
    RECEIVE_ITEMS = 'ADD_ITEMS',
    REMOVE_ITEMS = 'REMOVE_ITEMS'
}

export enum TransactionDirection {
    CREDIT = 'CREDIT',
    DEBIT = 'DEBIT',
    NEUTRAL = 'NEUTRAL'
}

export type TransactionSortField = 'createdAt' | 'amount' | 'type'
export type SortDirection = 'asc' | 'desc'

export interface ITransactionQuery {
    page: number
    limit: number
    userId?: string
    types?: string[]
    kinds?: string[]
    search?: string
    dateFrom?: string
    dateTo?: string
    amountMin?: number
    amountMax?: number
    sortField?: TransactionSortField
    sortDir?: SortDirection
    includeSummary?: boolean
}

export interface ITransactionSummary {
    totalCount: number
    totalCredits: number
    totalDebits: number
    net: number
    byType: Partial<Record<TransactionType, { count: number; amount: number }>>
}

export type TransactionBadgeColor = 'green' | 'red' | 'gray'

export interface ITransactionMeta {
    label: string
    direction: TransactionDirection
    color: TransactionBadgeColor
}

export const TRANSACTION_META: Record<TransactionType, ITransactionMeta> = {
    // Default for a credit. Wallet debits reuse this type with a negative amount;
    // `transactionDisplayMeta` flips the label, direction, and color in that case.
    [TransactionType.ADD_FUNDS]: { label: 'Add Funds', direction: TransactionDirection.CREDIT, color: 'green' },
    [TransactionType.DEPOSIT]: { label: 'Deposit', direction: TransactionDirection.CREDIT, color: 'green' },
    [TransactionType.CASE_WINNINGS]: { label: 'Case Winnings', direction: TransactionDirection.CREDIT, color: 'green' },
    [TransactionType.PACK_WINNINGS]: { label: 'Pack Winnings', direction: TransactionDirection.CREDIT, color: 'green' },
    [TransactionType.FREE_CASE_WINNINGS]: {
        label: 'Free Case Winnings',
        direction: TransactionDirection.CREDIT,
        color: 'green'
    },
    [TransactionType.FREE_PACK_WINNINGS]: {
        label: 'Free Pack Winnings',
        direction: TransactionDirection.CREDIT,
        color: 'green'
    },
    [TransactionType.AFFILIATE_EARNINGS]: {
        label: 'Affiliate Earnings',
        direction: TransactionDirection.CREDIT,
        color: 'green'
    },
    [TransactionType.ITEMS_SOLD]: { label: 'Items Sold', direction: TransactionDirection.CREDIT, color: 'green' },
    [TransactionType.PROMO_CODE]: { label: 'Promo Code', direction: TransactionDirection.CREDIT, color: 'green' },
    [TransactionType.LEADERBOARD_PAYOUT]: {
        label: 'Leaderboard Payout',
        direction: TransactionDirection.CREDIT,
        color: 'green'
    },

    [TransactionType.CASHOUT]: { label: 'Cashout', direction: TransactionDirection.DEBIT, color: 'red' },
    [TransactionType.CARD_PURCHASE]: { label: 'Card Purchase', direction: TransactionDirection.DEBIT, color: 'red' },
    [TransactionType.CARD_PURCHASED]: { label: 'Card Purchased', direction: TransactionDirection.DEBIT, color: 'red' },
    [TransactionType.CASE_PURCHASE]: { label: 'Case Purchase', direction: TransactionDirection.DEBIT, color: 'red' },
    [TransactionType.PACK_PURCHASE]: { label: 'Pack Purchase', direction: TransactionDirection.DEBIT, color: 'red' },
    [TransactionType.ITEMS_SALE]: { label: 'Items Sale', direction: TransactionDirection.DEBIT, color: 'red' },
    [TransactionType.SHIPPING]: { label: 'Shipping', direction: TransactionDirection.DEBIT, color: 'red' },
    [TransactionType.WITHDRAWAL]: { label: 'Withdrawal', direction: TransactionDirection.DEBIT, color: 'red' },

    // An accepted upgrade writes a pair, both stored as positive item values. The names
    // read from the house's side, so they invert for the user: `IN` is the cards taken
    // out of their withdraw list (REMOVE_ITEMS), `OUT` the one card they receive
    // (RECEIVE_ITEMS). Signed this way, the pair nets to the overpay the backend
    // budgets per user (OUT minus IN).
    [TransactionType.ITEMS_UPGRADE_IN]: {
        label: 'Upgrade Cards Given',
        direction: TransactionDirection.DEBIT,
        color: 'red'
    },
    [TransactionType.ITEMS_UPGRADE_OUT]: {
        label: 'Upgrade Card Received',
        direction: TransactionDirection.CREDIT,
        color: 'green'
    },

    [TransactionType.ITEMS_TRADE_IN]: {
        label: 'Items Trade In',
        direction: TransactionDirection.NEUTRAL,
        color: 'gray'
    },
    [TransactionType.ITEMS_TRADE_OUT]: {
        label: 'Items Trade Out',
        direction: TransactionDirection.NEUTRAL,
        color: 'gray'
    },
    [TransactionType.ADMIN_INVENTORY_ADD]: {
        label: 'Admin Inventory Add',
        direction: TransactionDirection.NEUTRAL,
        color: 'gray'
    },
    [TransactionType.ADMIN_INVENTORY_REMOVE]: {
        label: 'Admin Inventory Remove',
        direction: TransactionDirection.NEUTRAL,
        color: 'gray'
    }
}

export enum TransactionDataRef {
    CASE_HISTORY = 'CaseHistory',
    PACK_HISTORY = 'PackHistory',
    CARD_HISTORY = 'CardHistory',
    DEPOSIT = 'Deposit',
    CASHOUT = 'Cashout',
    WITHDRAWAL = 'Withdrawal',
    PROMO_CODE = 'PromoCode',
    LEADERBOARD = 'Leaderboard'
}
